import { APIClient, HTTPClientConfig } from '@innerhive-internal/innerhive-api-client';
import { State } from '@meraki-internal/state';
import { Logger } from '../support/Logger';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { nanoid } from 'nanoid';
import { ISO8601 } from '../support/ISO8601';
import { APP_FOREGROUNDED, APP_BACKGROUNDED } from '../AppContainer';
import { isPlatform } from '@ionic/react';
import jwtDecode from 'jwt-decode';

export interface IMessageFromSocket {
    action: string;
};

export interface IResourceChangedSocketMessage extends IMessageFromSocket {
    action: 'resourceChanged';
    resourceToRefresh: string;
    resourceChangeType: 'changed' | 'deleted';
};

/** What `web-sockets.connected` carries. */
export interface IWebSocketsConnected {
    // A socket was open before this one, or an attempt failed first, so the server may have sent something meanwhile
    mayHaveMissedMessages: boolean;
};

export interface IPingAckMessage extends IMessageFromSocket {
    action: 'ping-ack';
    messageId: string;
};

const MAX_BACKOFF_SECONDS = 5 * 60; // 5 minutes
const PING_INTERVAL_MS = 10 * 1000; // 10 seconds

// Our access tokens carry no exp; the server (TokenService.verify in innerhive-api) rejects any
// non-anonymous user token whose iat is older than a 30-day maxAge. Once we're past that, every
// websocket connect is guaranteed to fail auth, so we stop reconnecting rather than loop forever
// against a token we can locally prove is too old. The socket re-engages on the next app load, after
// a normal HTTP 401 refreshes the access token. Keep in sync with TokenService.verify's maxAge.
const MAX_TOKEN_AGE_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// native only: only rebuild the socket on foreground if the app was backgrounded at least this long.
// A brief interruption (Control Center, a permission/biometric prompt) fires a quick background->
// foreground pair that doesn't kill the socket, so rebuilding on it would just churn the connection.
const REBUILD_AFTER_BACKGROUND_MS = 10 * 1000; // 10 seconds

export class SocketState extends State<Record<string, never>> {
    static inject = () => [
        APIClient,
        Logger,
        HTTPClientConfig,
        BufferedEventBus
    ];
    constructor(
        private apiClient: APIClient,
        private logger: Logger,
        private httpConfig: HTTPClientConfig,
        private events: BufferedEventBus
    ){
        super({ });

        // Only NATIVE needs proactive foreground/background socket management. Backgrounding a native
        // app can leave a half-dead socket (client readyState still OPEN while the server has dropped
        // it), so on a real return-to-foreground we rebuild (close()+connect()), and on background we
        // stop pinging (battery). The rebuild is debounced on how long we were backgrounded: a brief
        // interruption (Control Center, a prompt) leaves the socket alive, so we just resume the
        // keepalive rather than churn a fresh connection on every quick background->foreground pair.
        //
        // On WEB we do neither: the socket stays alive across tab visibility changes, and
        // APP_FOREGROUNDED fires on EVERY visibilitychange — rebuilding a healthy web socket on each
        // one churns the connection (a tab whose visibility flaps reconnects every cycle, piling up
        // connection records). A genuinely-closed web socket self-heals via the close handler's
        // backoff reconnect, and the ForegroundReconciler catch-up still runs on web to converge state.
        this.events.on(APP_FOREGROUNDED, () => {
            if (!isPlatform('capacitor')) {
                return;
            }
            if (!this.webSocketUrl) {
                // init() never found a websocket url (or hasn't run yet); nothing to rebuild
                return;
            }
            const backgroundedMs = this.backgroundedAt ? Date.now() - this.backgroundedAt : Infinity;
            this.backgroundedAt = undefined;
            if (backgroundedMs < REBUILD_AFTER_BACKGROUND_MS) {
                // brief interruption — the socket is almost certainly still alive, so don't churn it.
                // resume the keepalive we stopped on background, but only if still connected; if a
                // reconnect is already in flight, its open handler will restart pinging.
                if (this.getConnectionStatus() === 'connected') {
                    this.debugLog('SocketState -- APP_FOREGROUNDED after brief interruption, resuming pinging');
                    this.stopPinging();
                    this.startPinging();
                }
                return;
            }
            this.debugLog('SocketState -- APP_FOREGROUNDED, rebuilding socket');
            this.close();
            this.connect();
        });

        this.events.on(APP_BACKGROUNDED, () => {
            if (!isPlatform('capacitor')) {
                return;
            }
            this.backgroundedAt = Date.now();
            this.debugLog('SocketState -- APP_BACKGROUNDED, stopping pinging');
            this.stopPinging();
        });
    }

    socket?: WebSocket;
    socketCount = 0;
    private hasOpened = false;
    private lostConnection = false;
    webSocketUrl?: string;
    backOffSeconds = 1;
    logs: string = '';
    debug = false;

    // native only: when the app was last backgrounded, so APP_FOREGROUNDED can debounce the rebuild
    private backgroundedAt?: number;

    private debugLog = (message: string) => {
        if (this.debug) {
            this.logs = this.logs + `${new Date().toLocaleTimeString()} ${message}\n`;
            this.logs = this.logs.split('\n').reverse().slice(0,250).reverse().join('\n');
            this.logger.info(message);
            this.setState({ });
        }
    };

    getConnectionStatus = (): 'connecting' | 'connected' | 'disconnected' | 'disconnecting' => {
        // https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/readyState
        if (this.socket && this.socket.readyState === 1){
            return 'connected';
        }
        if (this.socket && this.socket.readyState === 0){
            return 'connecting';
        }
        if (this.socket && this.socket.readyState === 2){
            return 'disconnecting';
        }
        if (this.socket && this.socket.readyState === 3){
            // now the socket considers itself closed, but
            // we expect the socket to be undefined, so our
            // state is that we're still disconnecting it
            return 'disconnecting';
        }
        else if (this.socket){
            // this should never happen b/c it should always have a
            // readyState (eg readyState: 0, connecting)
            return 'connecting';
        }
        else {
            return 'disconnected';
        }
    };

    getPrototocol = () => {
        return this.socket?.protocol || '';
    };

    private logError = (message: string) => {
        this.logs = this.logs + `${new Date().toLocaleTimeString()} ${message}\n`;
        this.logger.error(message);
        this.setState({ });
    };

    init = async () => {
        try {
            const entry = await this.apiClient.entry();

            this.webSocketUrl = entry.links.websockets?.href;

            if (!this.webSocketUrl){
                this.debugLog('SocketState.init -- no websocket url in entry resource so aborting socket init');
                return;
            }

            this.connect();
        }
        catch (err: any){
            this.logError(`SocketState.init -- failed to init. Will not try again til next app load. Error: ${err.toString()}`);
        }
    };

    // Don't attempt (or keep retrying) a connection with an access token we can locally tell the server
    // will reject for age — see MAX_TOKEN_AGE_MS. The default anonymous token never expires server-side,
    // and viewer tokens are governed by their own exp (and rejected from web sockets by tokenType anyway),
    // so neither is subject to the 30d maxAge. A token we can't decode is left for the server to judge.
    private isAccessTokenTooOldToConnect = (): boolean => {
        const token = this.httpConfig.accessToken;
        if (!token){
            return false;
        }
        try {
            const { iat, id, tokenType } = jwtDecode<{ iat?: number; id?: string; tokenType?: string }>(token);
            if (!iat || id === 'anonymous-default' || tokenType === 'caremap-viewer'){
                return false;
            }
            return Date.now() - (iat * 1000) >= MAX_TOKEN_AGE_MS;
        }
        catch (err){
            // can't decode the token — let the connect attempt proceed and let the server decide
            return false;
        }
    };

    connect = () => {
        this.lastFivePings = [];
        this.preventRetry = false;

        if (!this.webSocketUrl){
            this.debugLog('SocketState.connect -- no websocket url in entry resource so aborting');
            return;
        }

        if (this.isAccessTokenTooOldToConnect()){
            this.debugLog('SocketState.connect -- access token is older than the server max age, so not connecting (will reconnect on next app load after a token refresh)');
            return;
        }

        this.socketCount++;
        const socket = new WebSocket(this.webSocketUrl,
            // when adding a new protocol, cannot contain " " or ":"
            [
                // this is the protocol, used for content negotation
                // we should avoid it, but if necessary to make a breaking change
                // we do so by agreeing to this protocol (the server has to respond with the same protocol)
                'ih-sockets-v1',

                // auth in websockets is permanently immature
                // while there are a few acceptable ways, using the protocol is the best IMO (and others)
                `jwt__${this.httpConfig.accessToken}`
            ],
        );
        this.socket = socket;

        this.debugLog('SocketState.connect -- connecting');

        socket.addEventListener('open', () => {
            if (socket !== this.socket) {
                // replaced before it opened; close it rather than leave an idle orphan connection
                socket.close();
                return;
            }

            // next time we try and connect start at 1s backoff
            this.backOffSeconds = 1;

            this.debugLog('SocketState.connect -- event open');

            this.startPinging();

            // tell the reconcile layer we have an open connection so it can run a
            // catch-up reload — covers messages missed while disconnected/backgrounded
            const connected: IWebSocketsConnected = { mayHaveMissedMessages: this.hasOpened || this.lostConnection };
            this.hasOpened = true;
            this.events.emit('web-sockets.connected', connected);
        });

        socket.addEventListener('message', (event) => {
            if (socket !== this.socket) {
                // the server fans out to every registered connection, so a replaced one can repeat a change
                return;
            }

            this.debugLog(`SocketState -- event message ${event.data}`);

            let message: IMessageFromSocket | undefined;

            try {
                message = JSON.parse(event.data);
            }
            catch (err){
                // do nothing, undefiend message will be handled below
            }

            if (!message){
                this.logError(`SocketState -- unexpected message, likely failed to parse JSON from string "${event.data}"`);
            }
            else if (message.action === 'resourceChanged'){
                this.events.emit('web-sockets.message', JSON.parse(event.data));
            }
            else if (message.action === 'ping-ack'){
                // do nothing
                const ping = this.lastFivePings.find(p => p.messageId === (message as IPingAckMessage).messageId);
                if (ping){
                    ping.ackedAt = new Date().toISOString();
                }
            }
            else {
                this.debugLog(`SocketState -- unexpected message ${JSON.stringify(message)}`);
            }
        });

        socket.addEventListener('error', (event) => {
            // we don't get a meaningful error if the connection wasn't established
            // b/c we're not considered a trusted user agent
            // https://stackoverflow.com/questions/31002592/javascript-doesnt-catch-error-in-websocket-instantiation
            // so this error is mostly worthless
            // and we can't use it to determine if the erorr is network related (user problem / ignore)
            // or release / configuration related (escalate to developer / sentry)
            this.debugLog(`SocketState -- event error. ${JSON.stringify(event)}`);
        });

        socket.addEventListener('close', () => {
            if (socket !== this.socket) {
                // an already-detached socket; reconnecting from here would orphan the current one
                return;
            }

            // only closes we didn't cause reach here: close() and connectionFailedPing detach the socket first.
            // Usually it never opened (protocol, auth, bad network); the browser rarely closes an open one.

            this.debugLog(`SocketState event -- close`);
            this.stopPinging();
            this.socket = undefined;
            this.lostConnection = true;

            if (!this.preventRetry){
                this.backoffAndRetryConnect();
            }
        });

        this.debugLog('SocketState.connect -- sync return');
    };

    private backoffAndRetryConnect = () => {
        Promise.resolve().then(async () => {
            this.debugLog(`backing off ${this.backOffSeconds} seconds`);
            await new Promise(resolve => setTimeout(resolve, this.backOffSeconds * 1000));
            if (this.preventRetry || this.socket) {
                // closed on purpose, or something else reconnected while we waited
                return;
            }

            // if we have to try again, wait longer
            this.backOffSeconds = this.backOffSeconds * 2;
            this.backOffSeconds = Math.min(this.backOffSeconds, MAX_BACKOFF_SECONDS);
            this.setState({ });

            // try again
            this.connect();
        });
    };

    private preventRetry = false;

    close = () => {
        this.preventRetry = true;
        if (this.socket){
            try {
                this.stopPinging();
                this.socket.close();
                this.socket = undefined;
                this.setState({ });
            }
            catch (err: any){
                this.debugLog(`failed to close socket with error ${err.string()}`);
            }
        }
    };

    private pingTimeoutId?: NodeJS.Timeout;
    lastFivePings: { messageId: string; sentAt: ISO8601; ackedAt?: ISO8601; }[] = [];
    private startPinging = () => {
        // one loop only: extra loops multiply pings and trip the 5-unacked check on a healthy socket
        this.stopPinging();
        const ping = () => {
            if (this.socket && this.getConnectionStatus() === 'connected'){
                const msg = { action: 'ping', messageId: nanoid() };
                this.debugLog(`SocketState.send ${JSON.stringify(msg)}`);

                // this will not fail (in any way observable by the client) while "connected", even
                // if the connection is effectively closed (eg wifi is disabled)
                // we detect failure by looking for the ack from the server
                // we close the connection on 5 failed acks in a row (50 seconds)
                this.socket.send(JSON.stringify(msg));

                this.lastFivePings.push({
                    messageId: msg.messageId,
                    sentAt: new Date().toISOString(),
                });
                this.lastFivePings = this.lastFivePings.reverse().slice(0, 5).reverse();
                if (this.lastFivePings.length >= 5 && this.lastFivePings.every(p => !p.ackedAt)){
                    this.connectionFailedPing();
                    return;
                }
            }
            else {
                // not stopping ping b/c something else shoudl do that, and don't want to add complexity of this also trying to close
                // when it is likely already closing perfectly fine
                this.debugLog(`not sending ping, not connected. Got ${this.getConnectionStatus()}`);
            }

            this.pingTimeoutId = setTimeout(ping, PING_INTERVAL_MS);
        };
        ping();
    };

    private connectionFailedPing = () => {
        // the socket is dead; once detached, its own close handler ignores it, so only this connect() reconnects
        const dead = this.socket;
        this.socket = undefined;
        this.stopPinging();
        dead?.close();

        this.connect();
    };

    private stopPinging = () => {
        if (this.pingTimeoutId){
            clearTimeout(this.pingTimeoutId);
            this.pingTimeoutId = undefined;
        }
    };
}
